import axios from "../../util/axiosConfig";

export type Market = {
  id: number;
  title: string;
  description: string;
  thumbnail: string;
  rules: string;
  source_url: string;
  closes_at: string | number;
  status: string;
  result: string | null;
  resolution: string | null;
  yes_price: number;
  collateral: number;
  view_count?: number;
};
export type Order = {
  id: number;
  market_id: number;
  title: string;
  outcome: string;
  side: string;
  price: number;
  quantity: number;
  remaining: number;
  status: string;
};
export type Position = {
  market_id: number;
  title: string;
  outcome: string;
  quantity: number;
  reserved: number;
  cost: number;
};
export type Trade = Order & { created_at: string | number };
export type Summary = {
  availableCredit: number;
  reservedCredit: number;
  predictionPayout: number;
  predictionProfit: number;
};
export type Mine = {
  summary: Summary;
  markets: Market[];
  interests: { market_id: number }[];
  positions: Position[];
  orders: Order[];
  trades: Trade[];
};
export type Detail = {
  market: Market;
  book: { outcome: string; side: string; price: number; quantity: number }[];
  trades: Trade[];
};
export type Ranking = {
  userId: number;
  level: number;
  nickname: string;
  payout: number;
  profit: number;
  wins: number;
  settled: number;
  surprise: number | null;
};
export const marketApi = {
  recordView: (id: string, referrer: string, query: string) =>
    axios.post<{ viewCount: number }>(`/api/markets/${id}/view`, { referrer, query }),
  uploadThumbnail: (file: File) => {
    const data = new FormData();
    data.append("file", file);
    return axios.post<{ url: string }>("/api/upload/image", data);
  },
  list: (q = "", page = 1) =>
    axios.get<Market[]>("/api/markets", { params: { q, page } }),
  detail: (id: string) => axios.get<Detail>(`/api/markets/${id}`),
  mine: () => axios.get<Mine>("/api/markets/me"),
  ranking: (sort: string, period = "all") =>
    axios.get<Ranking[]>("/api/markets/ranking", { params: { sort, period } }),
  create: (body: unknown) => axios.post<{ id: number }>("/api/markets", body),
  update: (id: number, body: unknown) => axios.put(`/api/markets/${id}`, body),
  order: (id: string, body: unknown) =>
    axios.post(`/api/markets/${id}/orders`, body),
  cancel: (market: number, id: number) =>
    axios.delete(`/api/markets/${market}/orders/${id}`),
  interest: (id: string, enabled: boolean) =>
    axios.put(`/api/markets/${id}/interest`, null, { params: { enabled } }),
  resolve: (id: number, body: unknown) =>
    axios.post(`/api/markets/${id}/resolve`, body),
};
export function marketError(error: any): string {
  return (
    error?.response?.data?.message ||
    (error?.response?.status === 403
      ? "권한이 없습니다."
      : error?.response?.status === 401
        ? "로그인이 필요합니다."
        : "요청에 실패했습니다. 잠시 후 다시 시도하세요.")
  );
}
export function thumbnailUploadError(error: any): string {
  const status = error?.response?.status;
  const data = error?.response?.data;
  if (typeof data?.message === "string") return data.message;
  if (typeof data === "string" && data.length < 400 && !/[<>]/.test(data)) return data;
  if (status === 413) return "서버의 업로드 용량 제한을 초과했습니다. 더 작은 이미지로 시도하거나 운영 프록시의 업로드 제한을 확인하세요.";
  if (status === 404 || status === 405) return "이미지 업로드 API를 찾을 수 없습니다. 운영 백엔드 배포와 /api/upload/image 연결을 확인하세요.";
  if (status === 500) return "서버에서 이미지 업로드에 실패했습니다. 서버 로그와 업로드 폴더의 쓰기 권한을 확인하세요.";
  if (status === 502 || status === 503 || status === 504) return "이미지 업로드 서버에 연결하지 못했습니다. 잠시 후 다시 시도하세요.";
  if (!error?.response) return "이미지 업로드 서버에 연결하지 못했습니다. 네트워크 연결을 확인하세요.";
  return marketError(error);
}
export const formatCredit = (value: number) =>
  Number(value || 0).toLocaleString("ko-KR");
export const formatTime = (value: string | number) =>
  new Date(value).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" });
export function isClosed(market: Market) {
  return (
    market.status !== "OPEN" ||
    new Date(market.closes_at).getTime() <= Date.now()
  );
}
